import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import NewsCard from '@/components/base/NewsCard';
import {
  formatViews,
  getAdjacentArticles,
  getArticleById,
  getCategoryName,
  getHotArticles,
  getRelatedArticles,
} from '@/utils/content';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function NewsDetail() {
  const { id } = useParams();
  const article = id ? getArticleById(id) : undefined;
  const [copied, setCopied] = useState(false);

  useDocumentMeta(
    article ? `${article.title}｜瓜哥吃瓜` : '内容未找到｜瓜哥吃瓜',
    article ? article.excerpt : '这条内容可能已经被移动或整理，回到新瓜速递看看别的新鲜事。',
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setCopied(false);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 pt-16 md:pt-20 flex items-center justify-center px-4">
          <div className="text-center py-20">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-background-100 text-foreground-400 text-3xl">
              <i className="ri-emotion-sad-line" aria-hidden="true"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条瓜已经吃完了</h1>
            <p className="mt-3 text-sm text-foreground-500">内容可能已被整理或移动，去新瓜速递看看别的吧。</p>
            <Link
              to="/news"
              className="mt-6 inline-flex items-center gap-1 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回新瓜速递
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);
  const { prev, next } = getAdjacentArticles(article);
  const hot = getHotArticles().slice(0, 5);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      datePublished: article.date,
      dateModified: article.date,
      author: { '@type': 'Person', name: article.author },
      publisher: { '@type': 'Organization', name: '瓜哥吃瓜' },
      articleSection: getCategoryName(article.categorySlug),
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dfhov.cn/' },
        { '@type': 'ListItem', position: 2, name: '新瓜速递', item: 'https://dfhov.cn/news' },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd data={schema} />
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <div className="px-4 md:px-6 py-6 md:py-8 bg-background-100 border-b border-background-200">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '新瓜速递', to: '/news' },
                { label: getCategoryName(article.categorySlug), to: `/news?cat=${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            <article className="lg:col-span-8">
              <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-8">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/news?cat=${article.categorySlug}`}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {getCategoryName(article.categorySlug)}
                  </Link>
                  <span className="px-2.5 py-1 rounded-full text-xs bg-accent-100 text-accent-800">
                    今日份新鲜事
                  </span>
                </div>

                <h1 className="mt-4 font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>

                <p className="mt-4 text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>

                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-400 pb-5 border-b border-background-200">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line" aria-hidden="true"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {formatViews(article.views)} 阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {article.readTime}
                  </span>
                </div>

                <div className="mt-6 w-full h-56 md:h-80 rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - 瓜哥吃瓜`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="article-body mt-7 text-base md:text-[17px] text-foreground-800 leading-loose">
                  {article.content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>

                <div className="mt-7 pt-5 border-t border-background-200 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500 mr-1">标签：</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/news?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-4 p-4 rounded-lg bg-background-100">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-500 text-background-50 font-medium">
                      {article.author.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{article.author}</span>
                      <span className="block text-xs text-foreground-500">{article.authorRole}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={copied ? 'ri-check-line' : 'ri-link'} aria-hidden="true"></i>
                      {copied ? '链接已复制' : '复制链接'}
                    </button>
                    <Link
                      to="/news"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-list-check-2" aria-hidden="true"></i>
                      看更多新瓜
                    </Link>
                  </div>
                </div>
              </div>

              <nav className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4" aria-label="上一篇下一篇">
                {prev ? (
                  <Link
                    to={`/news/${prev.id}`}
                    className="group p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-1 text-xs text-foreground-400 mb-2">
                      <i className="ri-arrow-left-line" aria-hidden="true"></i>
                      上一篇
                    </span>
                    <span className="block text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {prev.title}
                    </span>
                  </Link>
                ) : (
                  <div className="p-4 rounded-lg border border-dashed border-background-300 text-xs text-foreground-400 flex items-center justify-center">
                    已经是第一篇了
                  </div>
                )}
                {next ? (
                  <Link
                    to={`/news/${next.id}`}
                    className="group p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer text-right"
                  >
                    <span className="flex items-center justify-end gap-1 text-xs text-foreground-400 mb-2">
                      下一篇
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </span>
                    <span className="block text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {next.title}
                    </span>
                  </Link>
                ) : (
                  <div className="p-4 rounded-lg border border-dashed border-background-300 text-xs text-foreground-400 flex items-center justify-center">
                    已经是最新一篇了
                  </div>
                )}
              </nav>

              <section className="mt-10" aria-labelledby="related-title">
                <h2 id="related-title" className="font-heading text-xl font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {related.map((item) => (
                    <NewsCard key={item.id} article={item} />
                  ))}
                </div>
              </section>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-5">
                <div className="rounded-lg bg-background-50 border border-background-200 p-5">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2 mb-3">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                    热门阅读
                  </h2>
                  <ol className="space-y-1">
                    {hot.map((item, index) => (
                      <li key={item.id}>
                        <Link
                          to={`/news/${item.id}`}
                          className="group flex items-start gap-3 py-3 border-b border-background-100 last:border-0 cursor-pointer"
                        >
                          <span
                            className={`font-heading text-base font-bold leading-none w-5 shrink-0 ${
                              index < 3 ? 'text-primary-500' : 'text-foreground-300'
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {item.title}
                            </span>
                            <span className="block mt-1 text-xs text-foreground-400">
                              {formatViews(item.views)} 阅读
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg bg-foreground-950 p-5">
                  <h2 className="font-heading text-lg font-bold text-background-50 mb-2">理性吃瓜提醒</h2>
                  <p className="text-sm text-background-300 leading-relaxed">
                    文中涉及的传闻，若暂无官方说明，我们会明确标注"尚未证实"。看热闹可以，别替别人下结论。
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}